// Auth provider — wraps the app and exposes the current user/session.
// Uses Supabase's onAuthStateChange (set up BEFORE getSession per docs).
//
// Also exports <IdleLogoutWatcher /> — AUTH-IDLE-1. Signs out and
// redirects to /auth?reason=idle after 5 minutes of inactivity. Paused
// only on /setup/payment (Stripe Embedded Checkout iframe steals focus
// from the parent window so we can't observe activity there). The watcher
// MUST be mounted inside both <BrowserRouter> (uses useLocation) and
// <AuthProvider> (uses useAuth) — see src/App.tsx.
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useLocation } from "react-router-dom";
import type { Session, User } from "@supabase/supabase-js";

// PERF-LCP-4 — dynamic import keeps the @supabase/* sub-packages
// (auth-js, realtime-js, storage-js, postgrest-js, phoenix, ~170 kB
// raw) out of the entry chunk. The supabase singleton loads only
// when AuthProvider first mounts. Other call-sites that statically
// import `@/integrations/supabase/client` are themselves inside lazy
// route chunks, so supabase ships in those chunks on-demand.
let supabaseModulePromise:
  | Promise<typeof import("@/integrations/supabase/client")>
  | null = null;
function loadSupabase() {
  if (!supabaseModulePromise) {
    supabaseModulePromise = import("@/integrations/supabase/client");
  }
  return supabaseModulePromise;
}

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let unsub: (() => void) | null = null;
    let cancelled = false;

    loadSupabase().then(({ supabase }) => {
      if (cancelled) return;
      // Listener FIRST, then hydrate from existing session.
      const { data: sub } = supabase.auth.onAuthStateChange((_event, newSession) => {
        setSession(newSession);
        setLoading(false);
      });
      unsub = () => sub.subscription.unsubscribe();

      supabase.auth.getSession().then(({ data, error }) => {
        // A stale refresh token in localStorage (e.g. user cleared cookies on
        // the server side, or the project was reset) makes Supabase log
        // "Invalid Refresh Token: Refresh Token Not Found" and leaves the
        // session null without clearing the bad token. Force a sign-out so
        // future calls start clean — otherwise the user can land in a state
        // where queries fire with no JWT and the page renders blank.
        if (error) {
          console.warn("[auth] getSession error — clearing stale session", error);
          supabase.auth.signOut().catch(() => {});
        }
        setSession(data.session);
        setLoading(false);
      });
    });

    return () => {
      cancelled = true;
      if (unsub) unsub();
    };
  }, []);

  const signOut = async () => {
    const { supabase } = await loadSupabase();
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user: session?.user ?? null, session, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

// ---------------------------------------------------------------------------
// AUTH-IDLE-1 — 5-minute idle logout watcher.
//
// Behaviour:
// - Active only while there is a session.
// - Activity events (mouse/keyboard/touch/scroll/visibility) reset the timer,
//   throttled to once per second.
// - PAUSED on /setup/payment only — the Stripe Embedded Checkout iframe
//   takes focus and we can't observe user activity inside it. /checkout/return
//   is NOT paused (it's a normal confirmation page).
// - Stripe postMessage from stripe.com origin also resets the timer as
//   defence-in-depth (the route pause is the primary protection).
// - On expiry: clear ONLY confetti.activeEventId (setup-draft keys are
//   untouched), sign out, redirect to /auth?mode=login&reason=idle.
// - Duplicate-expiry guard via hasExpiredRef ensures one signOut/redirect.
// - paused is in the effect deps, so leaving /setup/payment restarts the
//   timer from zero with a fresh 5-minute window.
// ---------------------------------------------------------------------------
const IDLE_TIMEOUT_MS = 5 * 60 * 1000;
const ACTIVITY_THROTTLE_MS = 1000;
const PAUSED_ROUTES = /^\/setup\/payment/;

export function IdleLogoutWatcher() {
  const { session } = useAuth();
  const location = useLocation();
  const paused = PAUSED_ROUTES.test(location.pathname);
  const hasExpiredRef = useRef(false);

  // Reset the one-shot guard when the user signs back in.
  useEffect(() => {
    if (!session) hasExpiredRef.current = false;
  }, [session]);

  useEffect(() => {
    if (!session || paused) return;

    const onExpire = async () => {
      if (hasExpiredRef.current) return;
      hasExpiredRef.current = true;
      try {
        localStorage.removeItem("confetti.activeEventId");
        const { supabase } = await loadSupabase();
        await supabase.auth.signOut();
      } finally {
        window.location.assign("/auth?mode=login&reason=idle");
      }
    };

    let timer = window.setTimeout(onExpire, IDLE_TIMEOUT_MS);
    let lastReset = Date.now();

    const reset = () => {
      const now = Date.now();
      if (now - lastReset < ACTIVITY_THROTTLE_MS) return;
      lastReset = now;
      window.clearTimeout(timer);
      timer = window.setTimeout(onExpire, IDLE_TIMEOUT_MS);
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") reset();
    };
    const onMessage = (e: MessageEvent) => {
      if (isStripeOrigin(e.origin)) reset();
    };

    const winEvents = [
      "mousemove",
      "mousedown",
      "keydown",
      "touchstart",
      "scroll",
    ] as const;
    winEvents.forEach((ev) =>
      window.addEventListener(ev, reset, { passive: true }),
    );
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("message", onMessage);

    return () => {
      window.clearTimeout(timer);
      winEvents.forEach((ev) => window.removeEventListener(ev, reset));
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("message", onMessage);
    };
  }, [session, paused]);

  return null;
}

// Exact-hostname check: only https://stripe.com or https://*.stripe.com.
// (A substring match would accept e.g. https://stripe.com.evil.example.)
export function isStripeOrigin(origin: unknown): boolean {
  if (typeof origin !== "string") return false;
  try {
    const u = new URL(origin);
    return u.protocol === "https:" && (u.hostname === "stripe.com" || u.hostname.endsWith(".stripe.com"));
  } catch {
    return false;
  }
}
